import React, { useState } from 'react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    employees: '',
    interest: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await fetch('https://formspree.io/f/mgoekqpj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          employees: '',
          interest: '',
          message: '',
        });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <div className="accent-bar accent-bar-center mb-lg"></div>
          <h1>Let's Talk</h1>
          <p>
            Ready to turn your data into an advantage? Schedule a free discovery call 
            or send us a message. No sales pitch — just an honest conversation.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Get in touch</h2>
              <p>
                We typically respond within 1 business day. For the fastest response, 
                schedule a call directly using the calendar below.
              </p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="contact-method-icon">📧</div>
                  <div>
                    <h4>Email</h4>
                    <p><a href="mailto:hello@lighthousedatapartners.com">hello@lighthousedatapartners.com</a></p>
                  </div>
                </div>
                <div className="contact-method">
                  <div className="contact-method-icon">📅</div>
                  <div>
                    <h4>Schedule a Call</h4>
                    <p>Book a free 30-minute discovery call</p>
                  </div>
                </div>
                <div className="contact-method">
                  <div className="contact-method-icon">💼</div>
                  <div>
                    <h4>LinkedIn</h4>
                    <p><a href="https://www.linkedin.com/in/lighthouse-data-partners-a9526a3a2/" target="_blank" rel="noopener noreferrer">Connect with us</a></p>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: 'var(--color-seafoam)', borderRadius: 'var(--radius-lg)' }}>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--color-navy)' }}>What happens next?</h4>
                <ol style={{ paddingLeft: '1.25rem', color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>We'll respond within 1 business day</li>
                  <li style={{ marginBottom: '0.5rem' }}>Schedule a 30-minute discovery call</li>
                  <li style={{ marginBottom: '0.5rem' }}>We'll learn about your situation</li>
                  <li>You'll get honest feedback on how we can help</li>
                </ol>
              </div>
            </div>

            <div className="contact-form-container">
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      className="form-input"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      className="form-input"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="company">Company *</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="form-input"
                      value={formData.company}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="employees">Number of Employees</label>
                    <select
                      id="employees"
                      name="employees"
                      className="form-select"
                      value={formData.employees}
                      onChange={handleChange}
                    >
                      <option value="">Select...</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-100">51-100</option>
                      <option value="101-250">101-250</option>
                      <option value="250+">250+</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="interest">What are you interested in?</label>
                  <select
                    id="interest"
                    name="interest"
                    className="form-select"
                    value={formData.interest}
                    onChange={handleChange}
                  >
                    <option value="">Select...</option>
                    <option value="foundation">Data Foundation Setup</option>
                    <option value="dashboards">Dashboards & Visibility</option>
                    <option value="forecasting">Forecasting & Planning</option>
                    <option value="automation">Automation & AI</option>
                    <option value="ongoing">Ongoing Support</option>
                    <option value="not-sure">Not sure yet — help me figure it out</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="message">Tell us about your situation</label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-textarea"
                    placeholder="What challenges are you facing? What would success look like?"
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                </div>

                {status === 'success' && (
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-seafoam)', borderRadius: 'var(--radius-md)', color: 'var(--color-navy)', textAlign: 'center', marginBottom: '1rem' }}>
                    ✓ Thanks for reaching out! We'll be in touch within 1 business day.
                  </div>
                )}

                {status === 'error' && (
                  <div style={{ padding: '1rem', backgroundColor: '#fee', borderRadius: 'var(--radius-md)', color: '#c00', textAlign: 'center', marginBottom: '1rem' }}>
                    Something went wrong. Please try again or email us directly.
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn btn-primary btn-lg form-submit" 
                  style={{ width: '100%' }}
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>

                <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '1rem' }}>
                  We'll never share your information or send you spam.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ-like section */}
      <section className="section principles-section">
        <div className="container">
          <div className="principles-header">
            <div className="accent-bar accent-bar-center mb-lg"></div>
            <h2>Common questions</h2>
          </div>

          <div className="principles-grid">
            <div className="principle-card">
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>How much does it cost?</h3>
              <p style={{ fontSize: '0.9375rem' }}>
                Projects typically range from $15K-$75K depending on scope. 
                Ongoing support starts at $3K/month. We'll give you a clear quote 
                after our discovery call.
              </p>
            </div>
            <div className="principle-card">
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>How long does it take?</h3>
              <p style={{ fontSize: '0.9375rem' }}>
                Most foundation projects take 4-8 weeks. Dashboard projects 
                are typically 2-4 weeks. We'll give you a realistic timeline 
                based on your specific situation.
              </p>
            </div>
            <div className="principle-card">
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>What if we're not sure what we need?</h3>
              <p style={{ fontSize: '0.9375rem' }}>
                That's totally fine — most clients start that way. Our discovery 
                call is designed to help you figure out the right starting point. 
                No commitment required.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
